package com.havn.app.features.auth.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.localization.HavnLanguage
import com.havn.app.core.localization.HavnLanguageFlag
import com.havn.app.core.localization.LocalHavnLanguage
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.ui.theme.LocalHavnThemeTokens
import havn.composeapp.generated.resources.Res
import havn.composeapp.generated.resources.havn_auth_logo
import org.jetbrains.compose.resources.painterResource

private const val AppStoreUrl = "https://apps.apple.com/us/app/havn-real-estate/id6772483170"
private const val GooglePlayUrl = "https://play.google.com/store/apps/details?id=binarts.havnre.app"

@Composable
fun AuthScreen(
    uiState: AuthUiState,
    onEmailChanged: (String) -> Unit,
    onOtpCodeChanged: (String) -> Unit,
    onSendEmailOtp: () -> Unit,
    onVerifyEmailOtp: () -> Unit,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onShowWelcome: () -> Unit,
    onShowEmailEntry: () -> Unit,
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit
) {
    when (uiState.step) {
        AuthStep.Welcome -> WelcomeScreen(
            uiState = uiState,
            onSignInWithGoogle = onSignInWithGoogle,
            onSignInWithApple = onSignInWithApple,
            onShowEmailEntry = onShowEmailEntry,
            selectedLanguage = selectedLanguage,
            onLanguageSelected = onLanguageSelected
        )
        AuthStep.EmailEntry -> EmailEntryScreen(
            uiState = uiState,
            onEmailChanged = onEmailChanged,
            onSendEmailOtp = onSendEmailOtp,
            onBack = onShowWelcome,
            selectedLanguage = selectedLanguage,
            onLanguageSelected = onLanguageSelected
        )
        AuthStep.VerifyCode -> VerifyCodeScreen(
            uiState = uiState,
            onOtpCodeChanged = onOtpCodeChanged,
            onVerifyEmailOtp = onVerifyEmailOtp,
            onResend = onSendEmailOtp,
            onBack = onShowEmailEntry,
            selectedLanguage = selectedLanguage,
            onLanguageSelected = onLanguageSelected
        )
    }
}

@Composable
private fun WelcomeScreen(
    uiState: AuthUiState,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onShowEmailEntry: () -> Unit,
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit
) {
    val copy = authCopy(LocalHavnLanguage.current)
    AuthFormFrame(
        subtitle = copy.welcomeSubtitle,
        showLogo = true,
        centerSubtitle = true,
        compact = true,
        selectedLanguage = selectedLanguage,
        onLanguageSelected = onLanguageSelected
    ) {
        if (HavnAuthPlatform.supportsAppleSignIn) {
            HavnButton(
                text = copy.continueWithApple,
                onClick = onSignInWithApple,
                enabled = !uiState.isSubmitting,
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Apple,
                modifier = Modifier.fillMaxWidth()
            )
        }
        HavnButton(
            text = copy.continueWithGoogle,
            onClick = onSignInWithGoogle,
            enabled = !uiState.isSubmitting,
            style = HavnButtonStyle.Secondary,
            leadingIcon = HavnIconKey.Google,
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = copy.continueWithEmail,
            onClick = onShowEmailEntry,
            enabled = !uiState.isSubmitting,
            leadingIcon = HavnIconKey.Mail,
            modifier = Modifier.fillMaxWidth()
        )
        if (HavnAuthPlatform.supportsAppleSignIn) {
            HelperText(copy.appleEmailHint)
        }
        AuthMessages(uiState)
        if (havnIsWebShell) {
            HelperText(copy.webNewAccountNote)
        }
        WebStoreBadges()
    }
}

@Composable
private fun EmailEntryScreen(
    uiState: AuthUiState,
    onEmailChanged: (String) -> Unit,
    onSendEmailOtp: () -> Unit,
    onBack: () -> Unit,
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit
) {
    val copy = authCopy(LocalHavnLanguage.current)
    AuthFormFrame(
        subtitle = copy.emailSubtitle,
        compact = true,
        selectedLanguage = selectedLanguage,
        onLanguageSelected = onLanguageSelected
    ) {
        HavnTextField(
            value = uiState.email,
            onValueChange = onEmailChanged,
            label = copy.email,
            isError = uiState.emailError != null,
            errorText = uiState.emailError,
            leadingIcon = HavnIconKey.Mail,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = copy.sendCode,
            onClick = onSendEmailOtp,
            enabled = !uiState.isSubmitting,
            loading = uiState.isSubmitting,
            leadingIcon = HavnIconKey.Mail,
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = copy.back,
            onClick = onBack,
            enabled = !uiState.isSubmitting,
            style = HavnButtonStyle.Ghost,
            leadingIcon = HavnIconKey.Back,
            modifier = Modifier.fillMaxWidth()
        )
        AuthMessages(uiState)
        WebStoreBadges()
    }
}

@Composable
private fun WebStoreBadges() {
    if (!havnIsWebShell) return
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val stack = maxWidth < 360.dp
        val arrangement = Arrangement.spacedBy(10.dp)
        if (stack) {
            Column(verticalArrangement = arrangement, modifier = Modifier.fillMaxWidth()) {
                StoreBadge(
                    icon = HavnIconKey.Apple,
                    eyebrow = "Download on the",
                    label = "App Store",
                    url = AppStoreUrl,
                    modifier = Modifier.fillMaxWidth()
                )
                StoreBadge(
                    icon = HavnIconKey.Google,
                    eyebrow = "Get it on",
                    label = "Google Play",
                    url = GooglePlayUrl,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        } else {
            Row(horizontalArrangement = arrangement, modifier = Modifier.fillMaxWidth()) {
                StoreBadge(
                    icon = HavnIconKey.Apple,
                    eyebrow = "Download on the",
                    label = "App Store",
                    url = AppStoreUrl,
                    modifier = Modifier.weight(1f)
                )
                StoreBadge(
                    icon = HavnIconKey.Google,
                    eyebrow = "Get it on",
                    label = "Google Play",
                    url = GooglePlayUrl,
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

@Composable
private fun StoreBadge(
    icon: HavnIconKey,
    eyebrow: String,
    label: String,
    url: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .height(54.dp)
            .clip(MaterialTheme.shapes.medium)
            .clickable { HavnExternalActions.openUrl(url) },
        shape = MaterialTheme.shapes.medium,
        color = theme.surface.copy(alpha = 0.82f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.9f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            HavnIcon(icon, contentDescription = null, tint = theme.primaryText, modifier = Modifier.size(24.dp))
            Column(verticalArrangement = Arrangement.spacedBy(1.dp)) {
                Text(eyebrow, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
                Text(
                    label,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
private fun VerifyCodeScreen(
    uiState: AuthUiState,
    onOtpCodeChanged: (String) -> Unit,
    onVerifyEmailOtp: () -> Unit,
    onResend: () -> Unit,
    onBack: () -> Unit,
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit
) {
    val copy = authCopy(LocalHavnLanguage.current)
    AuthFormFrame(
        subtitle = copy.verifySubtitle,
        compact = true,
        selectedLanguage = selectedLanguage,
        onLanguageSelected = onLanguageSelected
    ) {
        HavnTextField(
            value = uiState.otpCode,
            onValueChange = onOtpCodeChanged,
            label = copy.emailCode,
            isError = uiState.otpCodeError != null,
            errorText = uiState.otpCodeError,
            leadingIcon = HavnIconKey.Check,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = copy.verifyCode,
            onClick = onVerifyEmailOtp,
            enabled = !uiState.isSubmitting,
            loading = uiState.isSubmitting,
            leadingIcon = HavnIconKey.Check,
            modifier = Modifier.fillMaxWidth()
        )
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnButton(
                text = copy.back,
                onClick = onBack,
                enabled = !uiState.isSubmitting,
                style = HavnButtonStyle.Ghost,
                leadingIcon = HavnIconKey.Back,
                modifier = Modifier.weight(1f)
            )
            HavnButton(
                text = copy.resend,
                onClick = onResend,
                enabled = !uiState.isSubmitting,
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Mail,
                modifier = Modifier.weight(1f)
            )
        }
        AuthMessages(uiState)
    }
}

@Composable
private fun AuthFormFrame(
    subtitle: String,
    showLogo: Boolean = false,
    centerSubtitle: Boolean = false,
    compact: Boolean = false,
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit,
    content: @Composable () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val horizontalPadding = if (compact) 20.dp else 24.dp
    val verticalPadding = if (compact) 22.dp else 34.dp
    val logoSize = if (compact) 76.dp else 112.dp
    val cardPadding = if (compact) 14.dp else 18.dp
    val cardSpacing = if (compact) 10.dp else 14.dp
    val contentModifier = Modifier
        .widthIn(max = 420.dp)
        .fillMaxWidth()
    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(Brush.verticalGradient(listOf(theme.background, theme.surface, theme.background)))
            .verticalScroll(rememberScrollState())
            .padding(horizontal = horizontalPadding, vertical = verticalPadding),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (showLogo) {
            Image(
                painter = painterResource(Res.drawable.havn_auth_logo),
                contentDescription = "HAVN",
                modifier = Modifier.size(logoSize)
            )
            Spacer(Modifier.height(8.dp))
            Text(
                "HAVN AI Real Estate",
                color = theme.primaryText,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center,
                modifier = contentModifier
            )
            Spacer(Modifier.height(10.dp))
        }
        Text(
            subtitle,
            color = theme.secondaryText,
            style = MaterialTheme.typography.bodyLarge,
            textAlign = if (centerSubtitle) TextAlign.Center else TextAlign.Start,
            modifier = contentModifier
        )
        Spacer(Modifier.height(18.dp))
        Surface(
            color = theme.card,
            shape = MaterialTheme.shapes.large,
            border = BorderStroke(1.dp, theme.border),
            modifier = contentModifier
        ) {
            Column(
                modifier = Modifier.padding(cardPadding),
                verticalArrangement = Arrangement.spacedBy(cardSpacing)
            ) {
                content()
            }
        }
        Spacer(Modifier.height(12.dp))
        AuthLanguageFlags(
            selectedLanguage = selectedLanguage,
            onLanguageSelected = onLanguageSelected,
            modifier = contentModifier
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun AuthLanguageFlags(
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    FlowRow(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        maxItemsInEachRow = 7
    ) {
        HavnLanguage.entries.forEach { language ->
            val selected = language == selectedLanguage
            Surface(
                modifier = Modifier
                    .clip(MaterialTheme.shapes.small)
                    .clickable { onLanguageSelected(language) },
                shape = MaterialTheme.shapes.small,
                color = if (selected) theme.primaryAccent.copy(alpha = 0.22f) else theme.surface.copy(alpha = 0.62f),
                border = BorderStroke(
                    1.dp,
                    if (selected) theme.primaryAccent else theme.border.copy(alpha = 0.76f)
                )
            ) {
                HavnLanguageFlag(
                    language = language,
                    modifier = Modifier.padding(5.dp)
                )
            }
        }
    }
}

@Composable
private fun AuthMessages(uiState: AuthUiState) {
    uiState.statusMessage?.let { message ->
        HelperText(message)
    }
    uiState.submitError?.let { error ->
        Text(error, color = LocalHavnThemeTokens.current.danger, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
private fun HelperText(text: String) {
    Text(
        text,
        color = LocalHavnThemeTokens.current.mutedText,
        style = MaterialTheme.typography.bodySmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth()
    )
}

private data class AuthCopy(
    val welcomeSubtitle: String,
    val continueWithApple: String,
    val continueWithGoogle: String,
    val continueWithEmail: String,
    val appleEmailHint: String,
    val emailSubtitle: String,
    val email: String,
    val sendCode: String,
    val back: String,
    val verifySubtitle: String,
    val emailCode: String,
    val verifyCode: String,
    val resend: String,
    val webNewAccountNote: String
)

private fun authCopy(language: HavnLanguage): AuthCopy = when (language) {
    HavnLanguage.English -> AuthCopy(
        welcomeSubtitle = "Open houses, contacts, and follow-ups in one secure agent workspace.",
        continueWithApple = "Continue with Apple",
        continueWithGoogle = "Continue with Google",
        continueWithEmail = "Continue with Email",
        appleEmailHint = "When Apple asks, choose Share My Email so HAVN receives your real email address.",
        emailSubtitle = "Enter your email and we will send a sign-in code plus a magic link.",
        email = "Email",
        sendCode = "Send code",
        back = "Back",
        verifySubtitle = "Enter the code from your email, or open the magic link on this device.",
        emailCode = "Email code",
        verifyCode = "Verify code",
        resend = "Resend",
        webNewAccountNote = "New to HAVN? Continue with email or Google to create your agent account."
    )
    HavnLanguage.Croatian -> AuthCopy(
        welcomeSubtitle = "Dani otvorenih vrata, kontakti i praćenja u sigurnom radnom prostoru agenta.",
        continueWithApple = "Nastavi s Appleom",
        continueWithGoogle = "Nastavi s Googleom",
        continueWithEmail = "Nastavi e-poštom",
        appleEmailHint = "Kada Apple zatraži, odaberite Dijeli moju e-poštu kako bi HAVN primio vašu stvarnu adresu e-pošte.",
        emailSubtitle = "Unesite e-poštu i poslat ćemo vam kod za prijavu i čarobnu poveznicu.",
        email = "E-pošta",
        sendCode = "Pošalji kod",
        back = "Natrag",
        verifySubtitle = "Unesite kod iz e-pošte ili otvorite čarobnu poveznicu na ovom uređaju.",
        emailCode = "Kod iz e-pošte",
        verifyCode = "Potvrdi kod",
        resend = "Pošalji ponovno",
        webNewAccountNote = "Novi ste u HAVN-u? Nastavite e-poštom ili Googleom kako biste izradili račun agenta."
    )
    HavnLanguage.Portuguese -> AuthCopy(
        welcomeSubtitle = "Visitas abertas, contactos e acompanhamentos num espaço de trabalho seguro para agentes.",
        continueWithApple = "Continuar com Apple",
        continueWithGoogle = "Continuar com Google",
        continueWithEmail = "Continuar com e-mail",
        appleEmailHint = "Quando a Apple perguntar, escolha Partilhar o meu e-mail para que o HAVN receba o seu endereço real.",
        emailSubtitle = "Introduza o seu e-mail e enviaremos um código de acesso e uma ligação mágica.",
        email = "E-mail",
        sendCode = "Enviar código",
        back = "Voltar",
        verifySubtitle = "Introduza o código recebido por e-mail ou abra a ligação mágica neste dispositivo.",
        emailCode = "Código do e-mail",
        verifyCode = "Verificar código",
        resend = "Reenviar",
        webNewAccountNote = "É novo no HAVN? Continue com e-mail ou Google para criar a sua conta de agente."
    )
    HavnLanguage.Dutch -> AuthCopy(
        welcomeSubtitle = "Open huizen, contacten en opvolging in één veilige werkruimte voor makelaars.",
        continueWithApple = "Doorgaan met Apple",
        continueWithGoogle = "Doorgaan met Google",
        continueWithEmail = "Doorgaan met e-mail",
        appleEmailHint = "Kies Deel mijn e-mail wanneer Apple erom vraagt, zodat HAVN je echte e-mailadres ontvangt.",
        emailSubtitle = "Voer je e-mailadres in. We sturen een aanmeldcode en een magische link.",
        email = "E-mail",
        sendCode = "Code verzenden",
        back = "Terug",
        verifySubtitle = "Voer de code uit je e-mail in of open de magische link op dit apparaat.",
        emailCode = "E-mailcode",
        verifyCode = "Code verifiëren",
        resend = "Opnieuw verzenden",
        webNewAccountNote = "Nieuw bij HAVN? Ga door met e-mail of Google om je makelaarsaccount aan te maken."
    )
    HavnLanguage.Polish -> AuthCopy(
        welcomeSubtitle = "Dni otwarte, kontakty i dalsze działania w jednym bezpiecznym miejscu pracy agenta.",
        continueWithApple = "Kontynuuj z Apple",
        continueWithGoogle = "Kontynuuj z Google",
        continueWithEmail = "Kontynuuj przez e-mail",
        appleEmailHint = "Gdy Apple zapyta, wybierz Udostępnij mój e-mail, aby HAVN otrzymał Twój prawdziwy adres.",
        emailSubtitle = "Wprowadź e-mail, a wyślemy kod logowania i magiczny link.",
        email = "E-mail",
        sendCode = "Wyślij kod",
        back = "Wstecz",
        verifySubtitle = "Wprowadź kod z wiadomości e-mail lub otwórz magiczny link na tym urządzeniu.",
        emailCode = "Kod e-mail",
        verifyCode = "Zweryfikuj kod",
        resend = "Wyślij ponownie",
        webNewAccountNote = "Pierwszy raz w HAVN? Kontynuuj przez e-mail lub Google, aby utworzyć konto agenta."
    )
    HavnLanguage.Hungarian -> AuthCopy(
        welcomeSubtitle = "Nyílt napok, kapcsolatok és utánkövetések egy biztonságos ügynöki munkaterületen.",
        continueWithApple = "Folytatás Apple-lel",
        continueWithGoogle = "Folytatás Google-lel",
        continueWithEmail = "Folytatás e-maillel",
        appleEmailHint = "Amikor az Apple rákérdez, válassza az E-mail-címem megosztása lehetőséget, hogy a HAVN megkapja a valódi címét.",
        emailSubtitle = "Adja meg e-mail-címét, és küldünk egy bejelentkezési kódot és egy varázshivatkozást.",
        email = "E-mail",
        sendCode = "Kód küldése",
        back = "Vissza",
        verifySubtitle = "Adja meg az e-mailben kapott kódot, vagy nyissa meg a varázshivatkozást ezen az eszközön.",
        emailCode = "E-mail-kód",
        verifyCode = "Kód ellenőrzése",
        resend = "Újraküldés",
        webNewAccountNote = "Új a HAVN-ban? Folytassa e-maillel vagy Google-lel az ügynöki fiók létrehozásához."
    )
    HavnLanguage.Romanian -> AuthCopy(
        welcomeSubtitle = "Vizionări deschise, contacte și urmăriri într-un spațiu de lucru securizat pentru agenți.",
        continueWithApple = "Continuă cu Apple",
        continueWithGoogle = "Continuă cu Google",
        continueWithEmail = "Continuă cu e-mail",
        appleEmailHint = "Când Apple vă întreabă, alegeți Partajați adresa mea de e-mail pentru ca HAVN să primească adresa reală.",
        emailSubtitle = "Introduceți adresa de e-mail și vă vom trimite un cod de conectare și un link magic.",
        email = "E-mail",
        sendCode = "Trimite codul",
        back = "Înapoi",
        verifySubtitle = "Introduceți codul din e-mail sau deschideți linkul magic pe acest dispozitiv.",
        emailCode = "Cod e-mail",
        verifyCode = "Verifică codul",
        resend = "Retrimite",
        webNewAccountNote = "Sunteți nou în HAVN? Continuați cu e-mail sau Google pentru a crea contul de agent."
    )
    HavnLanguage.Slovak -> AuthCopy(
        welcomeSubtitle = "Prehliadky, kontakty a follow-upy v jednom bezpečnom pracovnom priestore agenta.",
        continueWithApple = "Pokračovať cez Apple",
        continueWithGoogle = "Pokračovať cez Google",
        continueWithEmail = "Pokračovať cez e-mail",
        appleEmailHint = "Keď sa Apple opýta, vyberte Zdieľať môj e-mail, aby HAVN dostal vašu skutočnú e-mailovú adresu.",
        emailSubtitle = "Zadajte e-mail a pošleme vám prihlasovací kód aj magický odkaz.",
        email = "E-mail",
        sendCode = "Odoslať kód",
        back = "Späť",
        verifySubtitle = "Zadajte kód z e-mailu alebo otvorte magický odkaz na tomto zariadení.",
        emailCode = "E-mailový kód",
        verifyCode = "Overiť kód",
        resend = "Odoslať znova",
        webNewAccountNote = "Ste v HAVN noví? Pokračujte e-mailom alebo cez Google a vytvorte si účet agenta."
    )
    HavnLanguage.German -> AuthCopy(
        welcomeSubtitle = "Besichtigungen, Kontakte und Follow-ups in einem sicheren Agenten-Arbeitsbereich.",
        continueWithApple = "Mit Apple fortfahren",
        continueWithGoogle = "Mit Google fortfahren",
        continueWithEmail = "Mit E-Mail fortfahren",
        appleEmailHint = "Wenn Apple fragt, wählen Sie Meine E-Mail teilen, damit HAVN Ihre echte E-Mail-Adresse erhält.",
        emailSubtitle = "Geben Sie Ihre E-Mail ein. Wir senden einen Anmeldecode und einen Magic Link.",
        email = "E-Mail",
        sendCode = "Code senden",
        back = "Zurück",
        verifySubtitle = "Geben Sie den Code aus Ihrer E-Mail ein oder öffnen Sie den Magic Link auf diesem Gerät.",
        emailCode = "E-Mail-Code",
        verifyCode = "Code prüfen",
        resend = "Erneut senden",
        webNewAccountNote = "Neu bei HAVN? Fahren Sie mit E-Mail oder Google fort, um Ihr Agentenkonto zu erstellen."
    )
    HavnLanguage.French -> AuthCopy(
        welcomeSubtitle = "Visites, contacts et relances dans un espace agent sécurisé.",
        continueWithApple = "Continuer avec Apple",
        continueWithGoogle = "Continuer avec Google",
        continueWithEmail = "Continuer avec l'e-mail",
        appleEmailHint = "Quand Apple le demande, choisissez Partager mon e-mail afin que HAVN reçoive votre vraie adresse.",
        emailSubtitle = "Saisissez votre e-mail. Nous vous enverrons un code de connexion et un lien magique.",
        email = "E-mail",
        sendCode = "Envoyer le code",
        back = "Retour",
        verifySubtitle = "Saisissez le code reçu par e-mail ou ouvrez le lien magique sur cet appareil.",
        emailCode = "Code e-mail",
        verifyCode = "Vérifier le code",
        resend = "Renvoyer",
        webNewAccountNote = "Nouveau sur HAVN ? Continuez avec l'e-mail ou Google pour créer votre compte agent."
    )
    HavnLanguage.Czech -> AuthCopy(
        welcomeSubtitle = "Prohlídky, kontakty a follow-upy v jednom bezpečném pracovním prostoru agenta.",
        continueWithApple = "Pokračovat přes Apple",
        continueWithGoogle = "Pokračovat přes Google",
        continueWithEmail = "Pokračovat přes e-mail",
        appleEmailHint = "Když se Apple zeptá, zvolte Sdílet můj e-mail, aby HAVN obdržel vaši skutečnou e-mailovou adresu.",
        emailSubtitle = "Zadejte e-mail a pošleme vám přihlašovací kód i magický odkaz.",
        email = "E-mail",
        sendCode = "Odeslat kód",
        back = "Zpět",
        verifySubtitle = "Zadejte kód z e-mailu nebo otevřete magický odkaz na tomto zařízení.",
        emailCode = "E-mailový kód",
        verifyCode = "Ověřit kód",
        resend = "Odeslat znovu",
        webNewAccountNote = "Jste v HAVN noví? Pokračujte e-mailem nebo přes Google a vytvořte si účet agenta."
    )
    HavnLanguage.Spanish -> AuthCopy(
        welcomeSubtitle = "Open houses, contactos y seguimientos en un espacio seguro para agentes.",
        continueWithApple = "Continuar con Apple",
        continueWithGoogle = "Continuar con Google",
        continueWithEmail = "Continuar con email",
        appleEmailHint = "Cuando Apple lo pregunte, elige Compartir mi email para que HAVN reciba tu dirección real.",
        emailSubtitle = "Introduce tu email y te enviaremos un código de acceso y un enlace mágico.",
        email = "Email",
        sendCode = "Enviar código",
        back = "Atrás",
        verifySubtitle = "Introduce el código de tu email o abre el enlace mágico en este dispositivo.",
        emailCode = "Código de email",
        verifyCode = "Verificar código",
        resend = "Reenviar",
        webNewAccountNote = "¿Nuevo en HAVN? Continúa con email o Google para crear tu cuenta de agente."
    )
    HavnLanguage.Italian -> AuthCopy(
        welcomeSubtitle = "Open house, contatti e follow-up in uno spazio agente sicuro.",
        continueWithApple = "Continua con Apple",
        continueWithGoogle = "Continua con Google",
        continueWithEmail = "Continua con email",
        appleEmailHint = "Quando Apple lo chiede, scegli Condividi la mia email così HAVN riceve il tuo vero indirizzo.",
        emailSubtitle = "Inserisci la tua email e ti invieremo un codice di accesso e un link magico.",
        email = "Email",
        sendCode = "Invia codice",
        back = "Indietro",
        verifySubtitle = "Inserisci il codice ricevuto via email o apri il link magico su questo dispositivo.",
        emailCode = "Codice email",
        verifyCode = "Verifica codice",
        resend = "Invia di nuovo",
        webNewAccountNote = "Nuovo su HAVN? Continua con email o Google per creare il tuo account agente."
    )
}
